import React from 'react';
import { Button, Flex, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  ChannelCategoryEntity,
  ChannelFilters,
  ChannelType,
  NO_CATEGORY_SLUG,
  countActiveFilters,
} from '../../../domain';

export interface ChannelFiltersChipsProps {
  filters: ChannelFilters;
  categories: ChannelCategoryEntity[];
  onChange: (filters: ChannelFilters) => void;
}

const ChannelFiltersChips: React.FC<ChannelFiltersChipsProps> = ({ filters, categories, onChange }) => {
  const { t } = useTranslation();

  if (countActiveFilters(filters) === 0) return null;

  const categoryLabel = (slug: string): string => {
    if (slug === NO_CATEGORY_SLUG) return t('channel-no-category', { ns: 'labels' });
    return categories.find((c) => c.slug === slug)?.name ?? slug;
  };

  const removeCategory = (slug: string) =>
    onChange({ ...filters, categories: (filters.categories ?? []).filter((s) => s !== slug) });

  const removeType = (type: ChannelType) =>
    onChange({ ...filters, types: (filters.types ?? []).filter((tp) => tp !== type) });

  return (
    <Flex gap={4} wrap align="center">
      {(filters.categories ?? []).map((slug) => (
        <Tag key={slug} closable onClose={() => removeCategory(slug)}>{categoryLabel(slug)}</Tag>
      ))}
      {(filters.types ?? []).map((type) => (
        <Tag key={type} closable onClose={() => removeType(type)}>
          {t(type === ChannelType.PAID ? 'channel-type-paid' : 'channel-type-free', { ns: 'labels' })}
        </Tag>
      ))}
      <Button type="link" size="small" onClick={() => onChange({ ...filters, categories: [], types: [] })}>
        {t('reset-filters', { ns: 'buttons' })}
      </Button>
    </Flex>
  );
};

export default ChannelFiltersChips;
